import { useEffect, useRef } from 'react';
import { CHANNELS, CLIENT_PROCESS_ID } from "./channels";
import { getConnectionInfo } from "../storage/localStorage";
import yjsDocumentService from '../sync/YjsDocumentService';
import { getWorkspaceId } from "../../shared/services/dbUtils";
import { isWorkspaceReadOnly } from "../../shared/services/workspaceAccess";

export const ConnectionStatusEnum = {
  CHECKING_LOGIN: 'Sprawdzanie statusu logowania...', NOT_LOGGED_IN: 'Nie zalogowany',
  WS_CONNECTED: 'Połączony i zalogowany', ERROR: 'Błąd podczas sprawdzania statusu logowania',
  WS_CONNECTION_DROPPED: 'Połączenie przerwane'
};

const config = {
  isCommonSessionEnabled: false, pollingDelay: 6000,
  currentSessionId: (getConnectionInfo() || {}).currentSessionId, sessionConnections: []
};

const channelInstances = {
  fileBrowserFileSelected: new CHANNELS.FILE_BROWSER_FILE_SELECTED(handleFileBrowserFileSelected)
};

function handleFileBrowserFileSelected(message) {
  if (!config.isCommonSessionEnabled) return;

  if (isWorkspaceReadOnly(getWorkspaceId()) && message.data.__source !== 'ws'
      && message.data.__processId === CLIENT_PROCESS_ID
      && message.data.fileName && Number.isSafeInteger(Number(message.data.songNumber))
      && Number(message.data.songNumber) > 0) {
    void submitAdoransSelection(message.data);
    return;
  }
  if (message.data.__source !== 'ws' && message.data.__processId === CLIENT_PROCESS_ID
      && message.data.fileName !== undefined && message.data.notifyOthers === true) {
    if (config.currentSessionId !== null) {
      updateSharedFileSelection(message.data);
    }
  }
}

async function submitAdoransSelection(selection) {
  try {
    const response = await fetch('/adorans/selection', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        fileName: selection.fileName,
        songNumber: Number(selection.songNumber),
        clientId: CLIENT_PROCESS_ID
      })
    });
    if (!response.ok) console.error('Adorans song selection was not synchronized:', response.status);
  } catch (error) {
    console.error('Adorans song selection was not synchronized:', error);
  }
}

// Update the shared file selection in YJS document
function updateSharedFileSelection(data) {
  try {
    const workspaceId = getWorkspaceId();
    const { doc } = yjsDocumentService.connectToDocument(workspaceId);

    // Use a special YJS map for file browser selections
    const selectionsMap = doc.getMap('fileBrowserSelections');

    // Store the selection with timestamp for ordering
    selectionsMap.set('latestSelection', {
      ...data
    });
  } catch (error) {
    console.error('Error updating shared file selection:', error);
  }
}

// Subscribe to file selection changes from YJS
function subscribeToFileSelectionChanges(callback) {
  try {
    const workspaceId = getWorkspaceId();
    const { doc } = yjsDocumentService.connectToDocument(workspaceId);
    const selectionsMap = doc.getMap('fileBrowserSelections');

    // Observe changes to the selections map
    // The Hosanna-backed map fires for every workspace-ui change (e.g. a presentation target),
    // so the same selection must not be applied again and scroll the view back to its song.
    let lastEventId;
    const observer = () => {
      const selection = selectionsMap.get('latestSelection');
      if (selection && selection.__processId !== CLIENT_PROCESS_ID) {
        if (selection.bridgeEventId && selection.bridgeEventId === lastEventId) return;
        lastEventId = selection.bridgeEventId;
        callback(selection);
      }
    };

    selectionsMap.observe(observer);
    observer();
    return () => selectionsMap.unobserve(observer);
  } catch (error) {
    console.error('Error subscribing to file selection changes:', error);
    return () => {};
  }
}

const EventBridgeComponent = ({ isCommonSessionEnabled, currentSessionId }) => {
  const unsubscribeFileSelectionRef = useRef(null);

  // Set up YJS file selection synchronization
  useEffect(() => {
    if (isCommonSessionEnabled) {
      // Clean up any existing subscription
      if (unsubscribeFileSelectionRef.current) {
        unsubscribeFileSelectionRef.current();
        unsubscribeFileSelectionRef.current = null;
      }
      // Subscribe to file selection changes
      unsubscribeFileSelectionRef.current = subscribeToFileSelectionChanges((selection) => {
        // Post message to local broadcast channel when a remote selection occurs
        channelInstances.fileBrowserFileSelected.postMessage({
          ...selection,
          __source: 'ws',
        });
      });
    }

    return () => {
      if (unsubscribeFileSelectionRef.current) {
        unsubscribeFileSelectionRef.current();
        unsubscribeFileSelectionRef.current = null;
      }
    };
  }, [isCommonSessionEnabled, currentSessionId]);

  useEffect(() => {
    config.isCommonSessionEnabled = isCommonSessionEnabled;
    if (config.currentSessionId !== currentSessionId) {
      config.currentSessionId = currentSessionId;
    }
  }, [isCommonSessionEnabled, currentSessionId]);

  return null;
};

export default EventBridgeComponent;
